import { Autocomplete, Skeleton, TextField } from '@mui/material';
import type { SlackUser } from '@vy/types';
import { isEmptyString, withDefault } from '@vy/utils';
import React, { useState } from 'react';
import { VyDialog } from '../../../../components/new-design/feedback/VyDialog';
import { useShareCreditNoteToSlack, useSlackGetUsers } from '../../../../hooks/requests/use-slack';
import { runMutation } from '../../../../utils/vy-mutation';

type ShareCreditNoteToSlackDialogProps = {
  open: boolean;
  onClose: () => void;
  creditLedgerEntryId: string;
};

export const ShareCreditNoteToSlackDialog: React.FC<ShareCreditNoteToSlackDialogProps> = ({
  open,
  onClose,
  creditLedgerEntryId,
}) => {
  const { data: slackUsersData, isLoading } = useSlackGetUsers({
    suspense: false,
    enabled: open,
  });
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  const shareCreditNoteToSlackMutation = useShareCreditNoteToSlack();

  const handleSubmit = async () => {
    await runMutation(
      shareCreditNoteToSlackMutation,
      { userId: selectedUserId, creditLedgerEntryId },
      {
        pending: 'Sharing credit note to Slack...',
        success: 'Credit note shared to Slack',
        error: 'Failed to share credit note to Slack',
      },
    );
    onClose();
    setSelectedUserId('');
  };

  const handleUserChange = (_event: React.SyntheticEvent, user: SlackUser | null) => {
    setSelectedUserId(user?.id ?? '');
  };

  const isSubmitDisabled = isEmptyString(selectedUserId) || isLoading;
  const slackUsers = withDefault(slackUsersData?.users, []);

  return (
    <VyDialog
      open={open}
      onClose={onClose}
      label="Share Credit Note with the team"
      cancelButtonText="Cancel"
      ctaDisabled={isSubmitDisabled}
      ctaText="Share"
      onSubmit={handleSubmit}
    >
      {isLoading ? (
        <Skeleton height={56} animation="wave" />
      ) : (
        <Autocomplete
          options={slackUsers}
          getOptionLabel={(user) => user.realName}
          onChange={handleUserChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Slack member"
              size="medium"
              sx={{ padding: '15px' }}
              data-vy="credit-note-share-slack-dialog-user-select"
            />
          )}
        />
      )}
    </VyDialog>
  );
};
